import React, { useEffect, useState } from 'react';
import {
  Card, Form, Input, Button, Typography, Alert, Avatar, Space, message,
  Tabs, Table, Tag, Modal, Popconfirm, Spin, Divider, InputNumber, Tooltip,
  ColorPicker, theme as antdTheme,
} from 'antd';
import {
  UserOutlined, LockOutlined, MailOutlined, SaveOutlined,
  PlusOutlined, DeleteOutlined, CheckCircleOutlined,
  CloudOutlined, ApiOutlined, ReloadOutlined, InfoCircleOutlined,
  UserAddOutlined, KeyOutlined, SafetyCertificateOutlined, RocketOutlined, GlobalOutlined,
  BgColorsOutlined, CheckOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { useTheme } from '../context/ThemeContext';

const { Title, Text } = Typography;

// ─────────────────────────────────────────────
// Profile + Password tab
// ─────────────────────────────────────────────
const ProfileTab = () => {
  const { token: themeToken } = antdTheme.useToken();
  const [profileForm]    = Form.useForm();
  const [passwordForm]   = Form.useForm();
  const [profileLoading, setProfileLoading]   = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [profileError,   setProfileError]     = useState('');
  const [passwordError,  setPasswordError]    = useState('');
  const [user, setUser]                       = useState(null);


  useEffect(() => {
    axios.get('/api/auth/me')
      .then(res => {
        setUser(res.data);
        profileForm.setFieldsValue({ name: res.data.name, email: res.data.email });
      })
      .catch(() => {});
  }, []);

  const handleProfileSave = async values => {
    setProfileLoading(true);
    setProfileError('');
    try {
      const res = await axios.put('/api/auth/me', { name: values.name });
      setUser(res.data);
      localStorage.setItem('userName', res.data.name || '');
      message.success('Profile updated');
    } catch (err) {
      setProfileError(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordSave = async values => {
    setPasswordLoading(true);
    setPasswordError('');
    try {
      await axios.put('/api/auth/me', { currentPassword: values.currentPassword, newPassword: values.newPassword });
      message.success('Password changed successfully');
      passwordForm.resetFields();
    } catch (err) {
      setPasswordError(err.response?.data?.message || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const initials = user?.name
    ? user.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
    : user?.email?.[0]?.toUpperCase() || '?';

  return (
    <div style={{ maxWidth: 600 }}>
      {/* Profile card */}
      <Card style={{ borderRadius: 14, border: `1px solid ${themeToken.colorBorderSecondary}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28 }}>
          <Avatar size={56} style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', fontSize: 20, fontWeight: 700 }}>
            {initials}
          </Avatar>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{user?.name || user?.email || '—'}</div>
            <div style={{ color: themeToken.colorTextSecondary, fontSize: 13 }}>{user?.email}</div>
          </div>
        </div>

        {profileError && <Alert message={profileError} type="error" showIcon closable onClose={() => setProfileError('')} style={{ marginBottom: 16, borderRadius: 8 }} />}

        <Form form={profileForm} layout="vertical" onFinish={handleProfileSave}>
          <Form.Item name="name" label="Full Name">
            <Input prefix={<UserOutlined style={{ color: themeToken.colorTextTertiary }} />} placeholder="Your name" style={{ borderRadius: 10 }} />
          </Form.Item>
          <Form.Item name="email" label="Email Address">
            <Input prefix={<MailOutlined style={{ color: themeToken.colorTextTertiary }} />} disabled style={{ borderRadius: 10 }} />
          </Form.Item>
          <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={profileLoading} style={{ borderRadius: 8 }}>
            Save Changes
          </Button>
        </Form>
      </Card>

      {/* Password card */}
      <Card style={{ borderRadius: 14, border: `1px solid ${themeToken.colorBorderSecondary}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <Text strong style={{ fontSize: 15, display: 'block', marginBottom: 20 }}>Change Password</Text>

        {passwordError && <Alert message={passwordError} type="error" showIcon closable onClose={() => setPasswordError('')} style={{ marginBottom: 16, borderRadius: 8 }} />}

        <Form form={passwordForm} layout="vertical" onFinish={handlePasswordSave}>
          <Form.Item name="currentPassword" label="Current Password" rules={[{ required: true, message: 'Enter your current password' }]}>
            <Input.Password prefix={<LockOutlined style={{ color: themeToken.colorTextTertiary }} />} placeholder="••••••••" style={{ borderRadius: 10 }} />
          </Form.Item>
          <Form.Item name="newPassword" label="New Password" rules={[{ required: true, min: 8, message: 'New password must be at least 8 characters' }]}>
            <Input.Password prefix={<LockOutlined style={{ color: themeToken.colorTextTertiary }} />} placeholder="Min 8 characters" style={{ borderRadius: 10 }} />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="Confirm New Password"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Please confirm your new password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) return Promise.resolve();
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined style={{ color: themeToken.colorTextTertiary }} />} placeholder="Repeat new password" style={{ borderRadius: 10 }} />
          </Form.Item>
          <Button type="primary" htmlType="submit" icon={<LockOutlined />} loading={passwordLoading} style={{ borderRadius: 8 }}>
            Change Password
          </Button>
        </Form>
      </Card>
    </div>
  );
};

// ─────────────────────────────────────────────
// Users tab
// ─────────────────────────────────────────────
const UsersTab = () => {
  const { token: themeToken } = antdTheme.useToken();
  const [users,       setUsers]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [modalOpen,   setModalOpen]   = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [form]                        = Form.useForm();

  const myEmail = localStorage.getItem('userEmail') || '';

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/settings/users');
      setUsers(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleCreate = async values => {
    setSaving(true);
    try {
      await axios.post('/api/settings/users', values);
      message.success(`User ${values.email} created`);
      form.resetFields();
      setModalOpen(false);
      fetchUsers();
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async id => {
    try {
      await axios.delete(`/api/settings/users/${id}`);
      message.success('User deleted');
      fetchUsers();
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to delete user');
    }
  };

  const columns = [
    {
      title: 'User', key: 'user',
      render: (_, r) => (
        <Space>
          <Avatar size={32} style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', fontSize: 12, fontWeight: 700 }}>
            {r.name ? r.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) : r.email[0].toUpperCase()}
          </Avatar>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{r.name || '—'}</div>
            <div style={{ color: themeToken.colorTextSecondary, fontSize: 12 }}>{r.email}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Created', dataIndex: 'created_at', width: 150,
      render: d => <Text style={{ fontSize: 12, color: themeToken.colorTextSecondary }}>{new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</Text>,
    },
    {
      title: 'Role', width: 80,
      render: (_, r) => <Tag color={r.email === myEmail ? 'processing' : 'default'}>{r.email === myEmail ? 'You' : 'Admin'}</Tag>,
    },
    {
      title: '', key: 'actions', width: 60, align: 'right',
      render: (_, r) => r.email === myEmail ? null : (
        <Popconfirm title={`Delete ${r.email}?`} description="This cannot be undone." onConfirm={() => handleDelete(r.id)} okText="Delete" okButtonProps={{ danger: true }}>
          <Button danger type="text" icon={<DeleteOutlined />} size="small" />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Text strong style={{ fontSize: 15 }}>Admin Users</Text>
          <div style={{ color: themeToken.colorTextSecondary, fontSize: 13 }}>All users who can log in and manage this platform</div>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)} style={{ borderRadius: 8, fontWeight: 600 }}>
          Create User
        </Button>
      </div>

      {error && <Alert message={error} type="error" showIcon closable onClose={() => setError('')} style={{ marginBottom: 16, borderRadius: 8 }} />}

      <Card style={{ borderRadius: 14, border: `1px solid ${themeToken.colorBorderSecondary}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <Table
          dataSource={users}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={false}
          size="small"
          scroll={{ x: 400 }}
        />
      </Card>

      <Modal
        title={<Space><div style={{ width: 32, height: 32, background: '#ede9fe', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><UserOutlined style={{ color: '#6366f1' }} /></div><span>Create New User</span></Space>}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); form.resetFields(); }}
        footer={null}
        destroyOnHidden
        width={440}
      >
        <Form form={form} layout="vertical" onFinish={handleCreate} style={{ marginTop: 16 }}>
          <Form.Item name="name" label="Full Name">
            <Input prefix={<UserOutlined style={{ color: themeToken.colorTextTertiary }} />} placeholder="e.g. Jane Smith" style={{ borderRadius: 10 }} />
          </Form.Item>
          <Form.Item name="email" label="Email Address" rules={[{ required: true, type: 'email', message: 'Enter a valid email' }]}>
            <Input prefix={<MailOutlined style={{ color: themeToken.colorTextTertiary }} />} placeholder="jane@example.com" style={{ borderRadius: 10 }} />
          </Form.Item>
          <Form.Item name="password" label="Password" rules={[{ required: true, min: 8, message: 'Password must be at least 8 characters' }]}>
            <Input.Password prefix={<LockOutlined style={{ color: themeToken.colorTextTertiary }} />} placeholder="Min 8 characters" style={{ borderRadius: 10 }} />
          </Form.Item>
          <Form.Item
            name="confirm"
            label="Confirm Password"
            dependencies={['password']}
            rules={[
              { required: true, message: 'Please confirm the password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) return Promise.resolve();
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined style={{ color: themeToken.colorTextTertiary }} />} placeholder="Repeat password" style={{ borderRadius: 10 }} />
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
            <Button onClick={() => { setModalOpen(false); form.resetFields(); }} style={{ borderRadius: 8 }}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={saving} style={{ borderRadius: 8 }}>Create User</Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

// ─────────────────────────────────────────────
// SES Setup Guide Modal
// ─────────────────────────────────────────────
const SES_STEPS = [
  {
    title: 'Create an IAM User',
    color: '#6366f1', bg: '#ede9fe',
    icon: <UserAddOutlined />,
    steps: [
      'Go to AWS Console → IAM → Users → Create user',
      'Give it a name like "jayho-mailer-ses"',
      'Attach the policy: AmazonSESFullAccess',
      'Complete creation — do NOT close the next screen',
    ],
  },
  {
    title: 'Generate Access Keys',
    color: '#f59e0b', bg: '#fef3c7',
    icon: <KeyOutlined />,
    steps: [
      'In the IAM user detail page, click "Security credentials" tab',
      'Scroll to "Access keys" → Create access key',
      'Choose "Application running outside AWS"',
      'Copy both the Access Key ID and Secret Access Key — the secret is shown only once',
      'Paste both values into the form on this page',
    ],
  },
  {
    title: 'Verify Your Sender Email',
    color: '#9DC533', bg: '#EEF6D0',
    icon: <SafetyCertificateOutlined />,
    steps: [
      'Go to AWS Console → Amazon SES → Verified Identities',
      'Click "Create identity" → choose Email address or Domain',
      'Enter the email you want to send from (e.g. noreply@yourdomain.com)',
      'AWS will send a verification email — click the link inside it',
      'Status will change to "Verified" once confirmed',
    ],
  },
  {
    title: 'Exit the SES Sandbox (Production Access)',
    color: '#3b82f6', bg: '#dbeafe',
    icon: <RocketOutlined />,
    steps: [
      'By default SES accounts are in sandbox — you can only send to verified emails',
      'To send to any email, go to SES → Account Dashboard → Request production access',
      'Fill in the use-case form — approval usually takes 24 hours',
      'Until approved, verify each recipient email address in Verified Identities',
    ],
  },
  {
    title: 'Set Your Region',
    color: '#8b5cf6', bg: '#f5f3ff',
    icon: <GlobalOutlined />,
    steps: [
      'Use the same AWS region where your SES identity is verified',
      'Common choices: us-east-1 (N. Virginia), eu-west-1 (Ireland), ap-south-1 (Mumbai)',
      'You can check your active region in the top-right corner of the AWS Console',
    ],
  },
];

const SesGuideModal = ({ open, onClose }) => {
  const { token: themeToken } = antdTheme.useToken();
  return (
  <Modal
    open={open}
    onCancel={onClose}
    footer={<Button type="primary" onClick={onClose} style={{ borderRadius: 8 }}>Got it</Button>}
    width={640}
    title={
      <Space>
        <div style={{ width: 32, height: 32, background: '#fef3c7', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CloudOutlined style={{ color: '#f59e0b' }} />
        </div>
        <span>AWS SES Setup Guide</span>
      </Space>
    }
    destroyOnHidden
    styles={{ body: { maxHeight: '65vh', overflowY: 'auto', paddingTop: 8 } }}
  >
    <Text style={{ color: themeToken.colorTextSecondary, fontSize: 13, display: 'block', marginBottom: 20 }}>
      Follow these steps to connect your AWS SES account and start sending emails.
    </Text>

    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {SES_STEPS.map((section, i) => (
        <div key={i} style={{ borderRadius: 12, border: `1px solid ${section.bg}`, overflow: 'hidden' }}>
          {/* Header */}
          <div style={{ background: section.bg, padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 28, height: 28, borderRadius: '50%',
              background: section.color,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: 14, flexShrink: 0,
            }}>
              {section.icon}
            </div>
            <Text strong style={{ color: section.color, fontSize: 14 }}>
              Step {i + 1}: {section.title}
            </Text>
          </div>
          {/* Steps */}
          <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {section.steps.map((s, j) => (
              <div key={j} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <div style={{
                  minWidth: 20, height: 20, borderRadius: '50%',
                  background: section.bg, border: `1.5px solid ${section.color}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 700, color: section.color, marginTop: 1,
                }}>
                  {j + 1}
                </div>
                <Text style={{ fontSize: 13, color: themeToken.colorText, lineHeight: '20px' }}>{s}</Text>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>

    <div style={{ marginTop: 16, padding: '12px 16px', background: themeToken.colorBgLayout, borderRadius: 10, border: `1px solid ${themeToken.colorBorderSecondary}` }}>
      <Text style={{ fontSize: 12, color: themeToken.colorTextSecondary }}>
        💡 <strong>Tip:</strong> After saving your credentials, use the <strong>Test Connection</strong> button to verify everything is working. It will show your daily send quota and current sending rate.
      </Text>
    </div>
  </Modal>
  );
};

// ─────────────────────────────────────────────
// SES / Email Config tab
// ─────────────────────────────────────────────
const SesTab = () => {
  const { token: themeToken } = antdTheme.useToken();
  const [form]          = Form.useForm();
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [testing,  setTesting]  = useState(false);
  const [error,    setError]    = useState('');
  const [testResult, setTestResult] = useState(null); // { ok, message, quota? }
  const [guideOpen, setGuideOpen]   = useState(false);


  useEffect(() => {
    axios.get('/api/settings/ses')
      .then(res => {
        form.setFieldsValue({
          ses_access_key_id:     res.data.ses_access_key_id     || '',
          ses_secret_access_key: res.data.ses_secret_access_key || '',
          ses_region:            res.data.ses_region            || 'us-east-1',
          ses_from_email:        res.data.ses_from_email        || '',
          ses_from_name:         res.data.ses_from_name         || '',
          ses_send_rate:         res.data.ses_send_rate ? parseInt(res.data.ses_send_rate, 10) : 14,
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async values => {
    setSaving(true);
    setError('');
    setTestResult(null);
    try {
      await axios.put('/api/settings/ses', values);
      message.success('SES settings saved');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    setError('');
    try {
      // Save current form values first so the test uses latest input
      const values = form.getFieldsValue();
      await axios.put('/api/settings/ses', values);
      const res = await axios.post('/api/settings/ses/test', {});
      setTestResult({ ok: true, message: res.data.message, quota: res.data.quota, region: res.data.region });
    } catch (err) {
      setTestResult({ ok: false, message: err.response?.data?.message || 'Connection failed' });
    } finally {
      setTesting(false);
    }
  };

  const AWS_REGIONS = [
    'us-east-1','us-east-2','us-west-1','us-west-2',
    'eu-west-1','eu-west-2','eu-west-3','eu-central-1',
    'ap-southeast-1','ap-southeast-2','ap-northeast-1','ap-south-1',
    'ca-central-1','sa-east-1',
  ];

  if (loading) return <div style={{ paddingTop: 60, textAlign: 'center' }}><Spin size="large" /></div>;

  return (
    <div style={{ maxWidth: 640 }}>
      <SesGuideModal open={guideOpen} onClose={() => setGuideOpen(false)} />
      <Card style={{ borderRadius: 14, border: `1px solid ${themeToken.colorBorderSecondary}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 40, background: '#fef3c7', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CloudOutlined style={{ color: '#f59e0b', fontSize: 20 }} />
            </div>
            <div>
              <Text strong style={{ fontSize: 15, display: 'block' }}>AWS SES Configuration</Text>
              <Text style={{ color: themeToken.colorTextSecondary, fontSize: 13 }}>Credentials for sending emails via Amazon Simple Email Service</Text>
            </div>
          </div>
          <Button
            icon={<InfoCircleOutlined />}
            onClick={() => setGuideOpen(true)}
            style={{ borderRadius: 8, borderColor: '#f59e0b', color: '#f59e0b', fontWeight: 600 }}
          >
            Setup Guide
          </Button>
        </div>

        {error && <Alert message={error} type="error" showIcon closable onClose={() => setError('')} style={{ marginBottom: 16, borderRadius: 8 }} />}

        <Form form={form} layout="vertical" onFinish={handleSave}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0 16px' }}>
            <Form.Item
              name="ses_access_key_id"
              label="AWS Access Key ID"
              rules={[{ required: true, message: 'Enter your AWS Access Key ID' }]}
            >
              <Input
                prefix={<ApiOutlined style={{ color: themeToken.colorTextTertiary }} />}
                placeholder="AKIA…"
                style={{ borderRadius: 10, fontFamily: 'monospace' }}
              />
            </Form.Item>

            <Form.Item
              name="ses_secret_access_key"
              label="AWS Secret Access Key"
              rules={[{ required: true, message: 'Enter your AWS Secret Access Key' }]}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: themeToken.colorTextTertiary }} />}
                placeholder="Existing key masked — enter new to change"
                style={{ borderRadius: 10, fontFamily: 'monospace' }}
              />
            </Form.Item>
          </div>

          <Form.Item
            name="ses_region"
            label="AWS Region"
            rules={[{ required: true, message: 'Enter the AWS region (e.g. us-east-1)' }]}
          >
            <Input
              placeholder="e.g. us-east-1"
              list="aws-regions"
              style={{ borderRadius: 10, fontFamily: 'monospace' }}
            />
            {/* datalist for region autocomplete */}
            <datalist id="aws-regions">
              {AWS_REGIONS.map(r => <option key={r} value={r} />)}
            </datalist>
          </Form.Item>

          <Divider style={{ margin: '8px 0 20px' }} />

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0 16px' }}>
            <Form.Item
              name="ses_from_email"
              label="From Email Address"
              rules={[{ required: true, type: 'email', message: 'Enter a valid verified email' }]}
              extra="Must be verified in AWS SES"
            >
              <Input
                prefix={<MailOutlined style={{ color: themeToken.colorTextTertiary }} />}
                placeholder="noreply@yourdomain.com"
                style={{ borderRadius: 10 }}
              />
            </Form.Item>

            <Form.Item
              name="ses_from_name"
              label="From Name"
            >
              <Input
                prefix={<UserOutlined style={{ color: themeToken.colorTextTertiary }} />}
                placeholder="Jay-Ho! Amplify"
                style={{ borderRadius: 10 }}
              />
            </Form.Item>
          </div>

          <Divider style={{ margin: '8px 0 20px' }} />

          <Form.Item
            name="ses_send_rate"
            label={
              <Space size={6}>
                Max Send Rate
                <Tooltip title="Maximum emails sent per second. SES sandbox: 1/s. SES production: check your account's sending rate in the AWS Console. The worker enforces this limit via BullMQ — restart the worker after changing this value.">
                  <InfoCircleOutlined style={{ color: themeToken.colorTextTertiary, cursor: 'pointer' }} />
                </Tooltip>
              </Space>
            }
            rules={[{ required: true, type: 'number', min: 1, max: 1000, message: 'Enter a value between 1 and 1000' }]}
            extra={
              <span style={{ fontSize: 12, color: themeToken.colorTextTertiary }}>
                SES sandbox default: <strong>1/s</strong> · SES production default: <strong>14/s</strong> · Check your AWS console for your actual limit
              </span>
            }
          >
            <InputNumber
              min={1}
              max={1000}
              step={1}
              addonAfter="emails / second"
              style={{ width: '100%', maxWidth: 280, borderRadius: 10 }}
            />
          </Form.Item>

          <div style={{ display: 'flex', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving} style={{ borderRadius: 8 }}>
              Save Settings
            </Button>
            <Button icon={<ReloadOutlined />} loading={testing} onClick={handleTest} style={{ borderRadius: 8 }}>
              Test Connection
            </Button>
          </div>
        </Form>
      </Card>

      {/* Test result */}
      {testResult && (
        <Alert
          type={testResult.ok ? 'success' : 'error'}
          showIcon
          icon={testResult.ok ? <CheckCircleOutlined /> : undefined}
          message={testResult.ok ? 'Connection successful' : 'Connection failed'}
          description={
            testResult.ok ? (
              <div style={{ marginTop: 4 }}>
                <Text style={{ fontSize: 13 }}>Region: <strong>{testResult.region}</strong></Text><br />
                {testResult.quota && (
                  <>
                    <Text style={{ fontSize: 13 }}>Daily send limit: <strong>{testResult.quota.max24HourSend}</strong></Text><br />
                    <Text style={{ fontSize: 13 }}>Sent last 24 h: <strong>{testResult.quota.sentLast24Hours}</strong></Text><br />
                    <Text style={{ fontSize: 13 }}>Max send rate: <strong>{testResult.quota.maxSendRate} emails/sec</strong></Text>
                  </>
                )}
              </div>
            ) : testResult.message
          }
          closable
          onClose={() => setTestResult(null)}
          style={{ borderRadius: 10 }}
        />
      )}

      {/* Info box */}
      <Card style={{ borderRadius: 14, border: '1px solid #e0f2fe', background: '#f0f9ff', boxShadow: 'none', marginTop: 16 }}>
        <Text strong style={{ color: '#0369a1', display: 'block', marginBottom: 8 }}>Getting started with AWS SES</Text>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {[
            '1. Create an IAM user with AmazonSESFullAccess permission in AWS Console',
            '2. Generate Access Key ID + Secret Access Key for that IAM user',
            '3. Verify your sender email address (or domain) in the SES console',
            '4. If your account is in SES sandbox, verify recipient emails too or request production access',
          ].map(step => (
            <Text key={step} style={{ color: '#0c4a6e', fontSize: 13 }}>{step}</Text>
          ))}
        </div>
      </Card>
    </div>
  );
};

// ─────────────────────────────────────────────
// Appearance tab
// ─────────────────────────────────────────────
const COLOR_PRESETS = [
  { label: 'Indigo',  value: '#6366f1' },
  { label: 'Violet',  value: '#8b5cf6' },
  { label: 'Blue',    value: '#3b82f6' },
  { label: 'Cyan',    value: '#06b6d4' },
  { label: 'Teal',    value: '#14b8a6' },
  { label: 'Emerald', value: '#9DC533' },
  { label: 'Rose',    value: '#f43f5e' },
  { label: 'Pink',    value: '#ec4899' },
  { label: 'Orange',  value: '#f97316' },
  { label: 'Amber',   value: '#f59e0b' },
  { label: 'Slate',   value: '#475569' },
  { label: 'Neutral', value: '#737373' },
];

const RADIUS_OPTIONS = [
  { label: 'Sharp',   value: 2,  preview: 2  },
  { label: 'Rounded', value: 8,  preview: 8  },
  { label: 'Pill',    value: 16, preview: 16 },
];

const SIDEBAR_STYLES = [
  {
    key: 'dark',
    label: 'Dark',
    description: 'Deep navy sidebar',
    sidebarBg: '#1e1b4b',
    textColor: 'rgba(255,255,255,0.6)',
    activeColor: '#fff',
  },
  {
    key: 'light',
    label: 'Light',
    description: 'Clean white sidebar',
    sidebarBg: '#ffffff',
    textColor: '#6b7280',
    activeColor: null, // use primary
  },
  {
    key: 'colored',
    label: 'Coloured',
    description: 'Sidebar matches accent',
    sidebarBg: null,   // use primary
    textColor: 'rgba(255,255,255,0.6)',
    activeColor: '#fff',
  },
];

// small mini sidebar preview card
const SidebarPreview = ({ style, colorPrimary, active }) => {
  const bg     = style.sidebarBg || colorPrimary;
  const actClr = style.activeColor || colorPrimary;
  const bars   = ['Dashboard', 'Lists', 'Campaigns'];
  return (
    <div style={{
      width: 80, height: 90, borderRadius: 8,
      background: bg, padding: '8px 6px',
      display: 'flex', flexDirection: 'column', gap: 4,
      border: active ? `2px solid ${colorPrimary}` : '2px solid transparent',
      boxShadow: active ? `0 0 0 2px ${colorPrimary}44` : '0 1px 4px rgba(0,0,0,0.12)',
      transition: 'border-color 0.15s',
      position: 'relative', overflow: 'hidden',
    }}>
      {/* logo stub */}
      <div style={{ width: 20, height: 8, borderRadius: 3, background: 'rgba(255,255,255,0.4)', marginBottom: 4 }} />
      {bars.map((b, i) => (
        <div key={b} style={{
          height: 10, borderRadius: 4,
          background: i === 0 ? actClr + 'cc' : style.textColor,
          opacity: i === 0 ? 1 : 0.5,
        }} />
      ))}
      {active && (
        <div style={{
          position: 'absolute', bottom: 4, right: 4,
          width: 14, height: 14, borderRadius: '50%',
          background: colorPrimary, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <CheckOutlined style={{ color: '#fff', fontSize: 8 }} />
        </div>
      )}
    </div>
  );
};

const AppearanceTab = () => {
  const { token: themeToken } = antdTheme.useToken();
  const { theme, setTheme } = useTheme();
  const [customColor, setCustomColor] = useState(theme.colorPrimary);
  const isPreset = COLOR_PRESETS.some(c => c.value === theme.colorPrimary);
  const isDark   = theme.mode === 'dark';

  const handlePreset  = (color) => { setCustomColor(color); setTheme({ colorPrimary: color }); };
  const handleCustom  = (_, hex) => { setCustomColor(hex); setTheme({ colorPrimary: hex }); };

  const cardStyle = {
    borderRadius: 14,
    border: `1px solid ${themeToken.colorBorderSecondary}`,
    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
    marginBottom: 20,
  };

  return (
    <div style={{ maxWidth: 640 }}>

      {/* ── 1. Light / Dark mode ── */}
      <Card style={cardStyle}>
        <Text strong style={{ fontSize: 15, display: 'block', marginBottom: 4 }}>Mode</Text>
        <Text style={{ color: themeToken.colorTextSecondary, fontSize: 13, display: 'block', marginBottom: 16 }}>
          Switch between light and dark interface
        </Text>
        <div style={{ display: 'flex', gap: 12 }}>
          {[
            {
              key: 'light', label: 'Light',
              icon: '☀️',
              bg: '#f8fafc', border: '#e2e8f0', textClr: '#111827', subClr: '#6b7280',
            },
            {
              key: 'dark',  label: 'Dark',
              icon: '🌙',
              bg: '#1e293b', border: '#334155', textClr: '#f1f5f9', subClr: '#94a3b8',
            },
          ].map(opt => (
            <button
              key={opt.key}
              onClick={() => setTheme({ mode: opt.key })}
              style={{
                flex: 1, minWidth: 120, height: 110, cursor: 'pointer',
                borderRadius: 12, border: theme.mode === opt.key
                  ? `2px solid ${theme.colorPrimary}`
                  : `2px solid ${isDark ? '#303030' : '#e5e7eb'}`,
                background: opt.bg,
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 8,
                position: 'relative', transition: 'border-color 0.15s',
                boxShadow: theme.mode === opt.key ? `0 0 0 3px ${theme.colorPrimary}22` : 'none',
              }}
            >
              {/* mock header */}
              <div style={{ position: 'absolute', top: 8, left: 8, right: 8, height: 10, borderRadius: 4, background: opt.border }} />
              <span style={{ fontSize: 22, marginTop: 12 }}>{opt.icon}</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: opt.textClr }}>{opt.label}</span>
              {theme.mode === opt.key && (
                <div style={{
                  position: 'absolute', top: 6, right: 6,
                  width: 18, height: 18, borderRadius: '50%',
                  background: theme.colorPrimary, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <CheckOutlined style={{ color: '#fff', fontSize: 10 }} />
                </div>
              )}
            </button>
          ))}
        </div>
      </Card>

      {/* ── 2. Accent colour ── */}
      <Card style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: '#f5f3ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <BgColorsOutlined style={{ fontSize: 20, color: '#8b5cf6' }} />
          </div>
          <div>
            <Text strong style={{ fontSize: 15, display: 'block' }}>Accent Colour</Text>
            <Text style={{ color: themeToken.colorTextSecondary, fontSize: 13 }}>Buttons, links, active states and highlights</Text>
          </div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 20 }}>
          {COLOR_PRESETS.map(({ label, value }) => (
            <Tooltip key={value} title={label} placement="top">
              <button
                onClick={() => handlePreset(value)}
                style={{
                  width: 36, height: 36, borderRadius: 10,
                  background: value, border: 'none', cursor: 'pointer',
                  boxShadow: theme.colorPrimary === value
                    ? `0 0 0 3px ${isDark ? '#1f1f1f' : '#fff'}, 0 0 0 5px ${value}`
                    : '0 1px 3px rgba(0,0,0,0.2)',
                  transition: 'box-shadow 0.15s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                {theme.colorPrimary === value && (
                  <CheckOutlined style={{ color: '#fff', fontSize: 13 }} />
                )}
              </button>
            </Tooltip>
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Text style={{ fontSize: 13, color: themeToken.colorTextSecondary }}>Custom:</Text>
          <ColorPicker value={customColor} onChange={handleCustom} showText size="middle" style={{ borderRadius: 10 }} />
          {!isPreset && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '4px 10px', borderRadius: 20,
              background: theme.colorPrimary + '22', border: `1px solid ${theme.colorPrimary}55`,
            }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: theme.colorPrimary }} />
              <Text style={{ fontSize: 12, color: theme.colorPrimary, fontWeight: 600 }}>Custom active</Text>
            </div>
          )}
        </div>
      </Card>

      {/* ── 3. Sidebar style ── */}
      <Card style={cardStyle}>
        <Text strong style={{ fontSize: 15, display: 'block', marginBottom: 4 }}>Sidebar Style</Text>
        <Text style={{ color: themeToken.colorTextSecondary, fontSize: 13, display: 'block', marginBottom: 16 }}>
          Choose the look of the navigation sidebar
        </Text>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {SIDEBAR_STYLES.map(s => (
            <div
              key={s.key}
              onClick={() => setTheme({ sidebarStyle: s.key })}
              style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}
            >
              <SidebarPreview style={s} colorPrimary={theme.colorPrimary} active={theme.sidebarStyle === s.key} />
              <Text style={{ fontSize: 12, fontWeight: theme.sidebarStyle === s.key ? 700 : 400, color: theme.sidebarStyle === s.key ? theme.colorPrimary : themeToken.colorTextSecondary }}>
                {s.label}
              </Text>
            </div>
          ))}
        </div>
      </Card>

      {/* ── 4. Corner radius ── */}
      <Card style={cardStyle}>
        <Text strong style={{ fontSize: 15, display: 'block', marginBottom: 4 }}>Corner Radius</Text>
        <Text style={{ color: themeToken.colorTextSecondary, fontSize: 13, display: 'block', marginBottom: 16 }}>Controls how rounded buttons, cards and inputs appear</Text>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {RADIUS_OPTIONS.map(({ label, value, preview }) => (
            <button
              key={value}
              onClick={() => setTheme({ borderRadius: value })}
              style={{
                width: 100, height: 72, cursor: 'pointer',
                border: theme.borderRadius === value ? `2px solid ${theme.colorPrimary}` : `2px solid ${isDark ? '#303030' : '#e5e7eb'}`,
                borderRadius: 12, background: isDark ? '#1f1f1f' : '#fff',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
                transition: 'border-color 0.15s',
              }}
            >
              <div style={{
                width: 56, height: 24, borderRadius: preview,
                background: theme.borderRadius === value ? theme.colorPrimary : (isDark ? '#374151' : '#e5e7eb'),
                transition: 'background 0.15s, border-radius 0.15s',
              }} />
              <Text style={{ fontSize: 12, color: theme.borderRadius === value ? theme.colorPrimary : '#6b7280', fontWeight: theme.borderRadius === value ? 600 : 400 }}>
                {label}
              </Text>
            </button>
          ))}
        </div>
      </Card>

      {/* ── 5. Live preview ── */}
      <Card style={{ ...cardStyle, marginBottom: 0 }}>
        <Text strong style={{ fontSize: 15, display: 'block', marginBottom: 12 }}>Live Preview</Text>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          <button style={{ background: theme.colorPrimary, color: '#fff', border: 'none', padding: '7px 18px', borderRadius: theme.borderRadius, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
            Primary
          </button>
          <button style={{ background: 'transparent', color: theme.colorPrimary, border: `1.5px solid ${theme.colorPrimary}`, padding: '7px 18px', borderRadius: theme.borderRadius, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
            Outline
          </button>
          <span style={{ color: theme.colorPrimary, fontSize: 13, fontWeight: 500, textDecoration: 'underline', cursor: 'pointer' }}>Link</span>
          <span style={{ background: theme.colorPrimary + '20', color: theme.colorPrimary, padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
            Tag
          </span>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '6px 12px', border: `1.5px solid ${isDark ? '#374151' : '#e5e7eb'}`,
            borderRadius: theme.borderRadius, fontSize: 13, color: isDark ? '#d1d5db' : '#374151',
            background: isDark ? '#1f1f1f' : '#fff',
          }}>
            Input field
          </div>
          <div style={{ padding: '6px 12px', borderRadius: theme.borderRadius, background: isDark ? '#1e293b' : '#f8fafc', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}` }}>
            <Text style={{ fontSize: 13 }}>Card</Text>
          </div>
        </div>
      </Card>
    </div>
  );
};

// ─────────────────────────────────────────────
// Main Settings page
// ─────────────────────────────────────────────
const Settings = () => {
  const { token: themeToken } = antdTheme.useToken();
  const tabItems = [
    {
      key:      'profile',
      label:    <Space><UserOutlined />Profile</Space>,
      children: <ProfileTab />,
    },
    {
      key:      'users',
      label:    <Space><UserOutlined />Users</Space>,
      children: <UsersTab />,
    },
    {
      key:      'ses',
      label:    <Space><CloudOutlined />Email (SES)</Space>,
      children: <SesTab />,
    },
    {
      key:      'appearance',
      label:    <Space><BgColorsOutlined />Appearance</Space>,
      children: <AppearanceTab />,
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>Settings</Title>
        <Text style={{ color: themeToken.colorTextSecondary }}>Manage your account, users, and email configuration</Text>
      </div>

      <Tabs
        defaultActiveKey="profile"
        items={tabItems}
        style={{ background: 'transparent' }}
      />
    </div>
  );
};

export default Settings;
